import React, { useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { clamp } from 'lodash';
import { asRect } from '../../maths/geometry/rect';
import { Rect } from '../../maths/geometry/rect/rectTypes';
import { PlatformDetails, OperatingSystem } from '../../platform/platformTypes';
import { Selection } from '@tiptap/pm/state';

type PaddingName = 'padding-top' | 'padding-right' | 'padding-bottom' | 'padding-left';

const PARAGRAPH_MARGIN_BOTTOM = 10;

const getStylePaddingValue = (styles: CSSStyleDeclaration, paddingName: PaddingName): number =>
    parseFloat(styles.getPropertyValue(paddingName)) + 1 || 0;

/**
 * The `editor.view.posAtCoords` method in Tiptap returns the document index accurately if the position
 * is within the editor's contents.
 * However, if the click occurs on the editor's padding, it can't determine the appropriate position.
 *
 * So when we detect that a click has occurred within the editor's padding, we clamp the click position
 * to the editor's contents, allowing Tiptap's position logic to work correctly.
 */
const getEditorContentsClickPosition = (event: React.MouseEvent): { left: number; top: number } => {
    const eventPos = { left: event.clientX, top: event.clientY };

    // If the event target is a paragraph or similar inside the editor, then we can use the default
    //  position handling to get an accurate position.
    if (!event.target) return eventPos;
    if (!(event.target instanceof HTMLElement)) return eventPos;
    if (!event.target.classList.contains('tiptap')) return eventPos;

    // Otherwise, the click has occurred on the editor boundary (in its padding)
    //  so we want to clamp the click position to the editor's contents, to allow the editor to
    //  accurately find a position
    const styles = window.getComputedStyle(event.target);
    const paddingTop = getStylePaddingValue(styles, 'padding-top');
    const paddingRight = getStylePaddingValue(styles, 'padding-right');
    // The margin bottom will cause the detected click position to be below the content and thus attempt to place
    //  the caret at the end. Instead, we want to place the caret at that position in the final line.
    const paddingBottom = getStylePaddingValue(styles, 'padding-bottom') + PARAGRAPH_MARGIN_BOTTOM;
    const paddingLeft = getStylePaddingValue(styles, 'padding-left');

    const boundingClientRect = event.target.getBoundingClientRect();

    return {
        left: clamp(eventPos.left, boundingClientRect.left + paddingLeft, boundingClientRect.right - paddingRight),
        top: clamp(eventPos.top, boundingClientRect.top + paddingTop, boundingClientRect.bottom - paddingBottom),
    };
};

/**
 * Finds the appropriate editor index to focus on based on the click position.
 */
const getEditorFocusIndex = (editor: Editor, event: React.MouseEvent, platformDetails: PlatformDetails): number => {
    // First, we need to bring the click position into the content rect to ensure that the position it finds
    //  is within a node, rather than on the boundary of nodes.
    // If we don't do this, the editor will return a position at the start of the next node, rather than in
    //  the current node - if the click occurs in the padding of the editor.
    const clickPosition = getEditorContentsClickPosition(event);

    const indexAtEnd = Selection.atEnd(editor.state.doc).from;
    // Even after doing this, the position might fall onto the next line, rather than the current line.
    //  E.g. Clicking the right-hand padding will return the position at the start of the next line, rather
    //  than the end of the current line. So we want to detect this and adjust the position accordingly.
    const posFromClick = editor.view.posAtCoords(clickPosition);
    const editorFocusIndex = posFromClick?.pos || indexAtEnd;

    const indexCoords = editor.view.coordsAtPos(editorFocusIndex);

    // The there's 2 indexes at the end of the editor (at the text & at the paragraph),
    // we just use the text one
    if (editorFocusIndex >= indexAtEnd) return indexAtEnd;

    const indexBefore = editorFocusIndex - 1;
    const indexBeforeCoords = editor.view.coordsAtPos(indexBefore);
    const indexAfter = editorFocusIndex + 1;

    const $editorFocusIndex = editor.state.doc.resolve(editorFocusIndex);
    const $indexAfter = editor.state.doc.resolve(indexAfter);

    // For some reason, ProseMirror seems to estimate iOS positions off by 1 index, so we detect that case
    // and adjust manually when it's occurring within a paragraph
    const isIos = platformDetails.os === OperatingSystem.iOS;

    const forceUseIndexBefore =
        isIos &&
        // If the estimated index has the same top position as the index before it we haven't crossed a line
        indexCoords.top === indexBeforeCoords.top &&
        // Has same parent (i.e. the estimated index is the next character in the same paragraph).
        // If we're at the end of a paragraph, the index after will be on the next line, so we don't want to change
        //  the index in that case
        $editorFocusIndex.sameParent($indexAfter) &&
        // If the click position has occurred on the right-hand side of the editor
        indexBeforeCoords.left < clickPosition.left;

    if (forceUseIndexBefore) return indexBefore;

    // If the coordinates of the index are above the click position, it means we're on the right line
    //  so we can just use that index. If the index is below the click position, we must be on the next line.
    if (indexCoords.top < clickPosition.top) return editorFocusIndex;

    // If the previous index is on the same line as the click position, we can use that index instead.
    if (indexBeforeCoords.top < clickPosition.top && indexBeforeCoords.bottom > clickPosition.top) return indexBefore;

    // Otherwise, stick with the original index, as it is the closest one to the click position
    return editorFocusIndex;
};

interface UseElementTiptapEditorClickHandlerProps {
    isSingleSelected: boolean;
    editor: Editor;
    isEditingThisEditor: boolean;
    isEditable: boolean;
    editorId: string;
    editorKey: string;
    platformDetails: PlatformDetails;
    startEditing?: (args: { editorId: string; editorKey: string; editorFocusRect: Rect }) => void;
}

/**
 * Triggers the startEditing function when the editor is clicked & editable.
 * Also finds the caret position based on the click position and focuses the editor at that position.
 */
const useElementTiptapEditorClickHandler = ({
    isSingleSelected,
    editor,
    isEditingThisEditor,
    isEditable,
    editorId,
    editorKey,
    platformDetails,
    startEditing,
}: UseElementTiptapEditorClickHandlerProps) =>
    useCallback(
        (event: React.MouseEvent) => {
            if (!isEditable) {
                // Consume click events when the element is in read-only mode, otherwise the
                // "sign up to add feedback" popup will show whenever a guest selects some text.
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            if (!startEditing) return;
            if (!isSingleSelected) return;
            if (isEditingThisEditor) return;

            const caretIndex = getEditorFocusIndex(editor, event, platformDetails);
            const caretCoords = editor.view.coordsAtPos(caretIndex);

            // Pass in editorFocusRect to startEditing, to make sure app scrolls properly
            // to the editor focus position. See mobileEditingMiddleware.ts.
            const editorFocusRect = asRect(caretCoords);
            startEditing({ editorId, editorKey, editorFocusRect });

            editor.commands.focus(caretIndex);
        },
        [isSingleSelected, editor, isEditingThisEditor, isEditable, editorId, editorKey, platformDetails],
    );

export default useElementTiptapEditorClickHandler;
